/**
 * RoleGate Component
 * 
 * Conditionally renders children based on user permissions/roles.
 * Use to hide UI elements user doesn't have permission to see.
 * 
 * SECURITY: This is for UX only! Server MUST enforce permissions.
 * Never rely on client-side checks for actual security.
 * 
 * @example
 * // Require specific permission
 * <RoleGate permission="threat:eliminate">
 *   <EliminateButton />
 * </RoleGate>
 * 
 * // Require any of multiple permissions
 * <RoleGate anyPermission={['threat:eliminate', 'threat:isolate']}>
 *   <ActionButtons />
 * </RoleGate>
 * 
 * // Require minimum role
 * <RoleGate minRole="admin">
 *   <AdminPanel />
 * </RoleGate>
 */

import { memo, type ReactNode } from 'react';
import { useAuth, type Permission, type Role } from '../../hooks/useAuth';

// ============================================================================
// Types
// ============================================================================

interface RoleGateProps {
  /** Require this specific permission */
  permission?: Permission;
  
  /** Require ALL of these permissions */
  allPermissions?: Permission[];
  
  /** Require ANY of these permissions */
  anyPermission?: Permission[];
  
  /** Require at least this role level */
  minRole?: Role;
  
  /** Content to show if user HAS permission */
  children: ReactNode;
  
  /** Optional content to show if user LACKS permission */
  fallback?: ReactNode;
  
  /** If true, show nothing instead of fallback when unauthorized */
  hideOnUnauthorized?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const RoleGate = memo(function RoleGate({
  permission,
  allPermissions,
  anyPermission,
  minRole,
  children,
  fallback = null,
  hideOnUnauthorized = true,
}: RoleGateProps) {
  const { hasPermission, hasAllPermissions, hasAnyPermission, hasMinimumRole, isLoading } = useAuth();

  // While loading auth state, show nothing (avoid flash)
  if (isLoading) {
    return null;
  }

  let isAuthorized = true;

  // Check single permission
  if (permission) {
    isAuthorized = isAuthorized && hasPermission(permission);
  }

  // Check all permissions
  if (allPermissions && allPermissions.length > 0) {
    isAuthorized = isAuthorized && hasAllPermissions(allPermissions);
  }

  // Check any permission
  if (anyPermission && anyPermission.length > 0) {
    isAuthorized = isAuthorized && hasAnyPermission(anyPermission);
  }

  // Check minimum role
  if (minRole) {
    isAuthorized = isAuthorized && hasMinimumRole(minRole);
  }

  // Render based on authorization
  if (isAuthorized) {
    return <>{children}</>;
  }

  if (hideOnUnauthorized) {
    return null;
  }

  return <>{fallback}</>;
});

// ============================================================================
// Convenience Wrapper Components
// ============================================================================

/**
 * Require admin role to view content.
 */
export const AdminOnly = memo(function AdminOnly({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <RoleGate minRole="admin" fallback={fallback}>
      {children}
    </RoleGate>
  );
});

/**
 * Require super_admin role to view content.
 */
export const SuperAdminOnly = memo(function SuperAdminOnly({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <RoleGate minRole="super_admin" fallback={fallback}>
      {children}
    </RoleGate>
  );
});

/**
 * Require security_analyst or higher to view content.
 */
export const SecurityAnalystOnly = memo(function SecurityAnalystOnly({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <RoleGate minRole="security_analyst" fallback={fallback}>
      {children}
    </RoleGate>
  );
});

export default RoleGate;

